import { useApolloClient } from "@apollo/client";
import debounce from "@mui/material/utils/debounce";
import { NotificationChannel, useMessageChannels } from "@sinch/components/application/Messages/useMessageChannels";
import { useWorker } from "@sinch/components/application/Worker/hooks";
import { AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";
import { RoleStatusEnum, UserRoleEnum } from "@sinch/core/entities/serverEnums";
import { useFormat } from "@sinch/core/hooks/format";
import { useLazyQueryHandler } from "@sinch/hooks/useLazyQueryHandler";
import { RoleStatusEnum as RoleStatus } from "@sinch/types/sinch.types";
import { intersection, isNil, propEq } from "ramda";
import { isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import { useCallback, useEffect, useMemo } from "react";
import { Recipient, WorkersAvailableChannels } from "./queries.graphql";
import {
  RecipientFragment,
  WorkersAvailableChannelsQuery,
  WorkersAvailableChannelsQueryVariables,
} from "./queries.types";
import { darken, Stack, Typography, useTheme } from "@mui/material";
import { useUserRole } from "@sinch/components/application/User/useUserRole";
import { useTranslation } from "react-i18next";
import { IconTypography } from "@sinch/components/typography/IconTypography/IconTypography";
import { useRoleStatus } from "@sinch/components/application/Worker/useRoleStatus";
import { StatusChip } from "@sinch/components/StatusChip/StatusChip";
import { MdsIcon } from "@sinch/components";

export const RECIPIENTS_OPTIONS_LIMIT = 30;

interface RecipientsOptions extends AutocompleteOptions {
  availableChannels?: string[];
  roleStatus?: RoleStatusEnum | RoleStatus;
}

export const useRecipients = ({
  value,
  channels,
  channelAddressProp,
  showRole,
  roles,
}: {
  value: string[];
  channels: NotificationChannel[];
  channelAddressProp: "phone" | "email";
  showRole?: boolean;
  roles?: UserRoleEnum[];
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { phone: formatPhone } = useFormat();
  const client = useApolloClient();
  const { getWorkerName } = useWorker();
  const { getRoleLabel } = useUserRole();
  const { getChannelOffIcon } = useMessageChannels();
  const { getRoleStatusIcon, getRoleStatusLabel, getRoleStatusColor } = useRoleStatus();

  // The dropdown options come from the search query. Resolving already-selected
  // recipients by id runs on a *separate* query handler so that selecting an
  // option never collapses the dropdown to just the selected ids - a shared
  // handler would overwrite `data` (which drives `options`) on every value change.
  const { data, dispatch, loading, previousData } = useLazyQueryHandler<
    WorkersAvailableChannelsQuery,
    WorkersAvailableChannelsQueryVariables
  >(WorkersAvailableChannels);

  const {
    data: valuesData,
    dispatch: dispatchValues,
    loading: valuesLoading,
    called: valuesCalled,
    previousData: valuesPreviousData,
  } = useLazyQueryHandler<WorkersAvailableChannelsQuery, WorkersAvailableChannelsQueryVariables>(
    WorkersAvailableChannels
  );

  // Shared mapping from a user fragment to an option. Reused for both the search
  // dropdown and the resolution of already-selected recipients (so their tags get
  // a proper label instead of falling back to the raw id).
  const buildRecipientOption = useCallback(
    ({ id, email, phone, availableNotificationChannels, roleStatus, role, ...worker }: RecipientFragment): RecipientsOptions => {
      const channelAddressLabel = channelAddressProp === "phone" && phone ? formatPhone.national(phone) : email;

      const firstUnavailable = channels.find((channel) => !availableNotificationChannels.includes(channel));

      return {
        value: id,
        label: `${getWorkerName(worker)} [${channelAddressLabel}]`,
        option: (
          <Stack alignItems="baseline" direction="row" justifyContent="space-between" width="100%">
            <Stack alignItems="center" direction="row" gap={1}>
              <span>{`${getWorkerName(worker)} [${channelAddressLabel}]`}</span>
              {roleStatus !== RoleStatusEnum.Active && (
                <StatusChip
                  label={
                    <Stack direction="row" gap={0.5}>
                      <MdsIcon fontSize="xs" icon={getRoleStatusIcon(roleStatus)} />
                      {getRoleStatusLabel(roleStatus)}
                    </Stack>
                  }
                  size="small"
                  sx={{
                    backgroundColor: getRoleStatusColor(roleStatus),
                    color: theme.palette.getContrastText(darken(getRoleStatusColor(roleStatus), 0.1)),
                    lineHeight: "1rem",
                  }}
                />
              )}
            </Stack>
            {firstUnavailable && (
              <IconTypography
                color="error"
                icon={getChannelOffIcon(firstUnavailable)}
                iconColor={({ palette }) => palette.error.main}
                iconSize="xs"
                variant="descriptionSmall"
              >
                {t("Message.channelUnavailable")}
              </IconTypography>
            )}
            {!firstUnavailable && showRole && (
              <Typography sx={({ palette }) => ({ color: palette.grey[500] })} variant="descriptionTiny">
                {role.map(getRoleLabel).join(", ")}
              </Typography>
            )}
          </Stack>
        ),
        availableChannels: availableNotificationChannels ?? [],
        roleStatus: roleStatus,
      };
    },
    [
      channelAddressProp,
      channels,
      formatPhone,
      getWorkerName,
      getRoleLabel,
      getChannelOffIcon,
      getRoleStatusIcon,
      getRoleStatusLabel,
      getRoleStatusColor,
      showRole,
      theme,
      t,
    ]
  );

  const options = useMemo<RecipientsOptions[]>(
    () => (data?.users ?? previousData?.users)?.map(buildRecipientOption) ?? [],
    [previousData?.users, data?.users, buildRecipientOption]
  );

  const debounceDispatch = useCallback(
    (...args: Parameters<typeof dispatch>) => debounce(dispatch, 200)(...args),
    [dispatch]
  );

  const debounceDispatchValues = useCallback(
    (...args: Parameters<typeof dispatchValues>) => debounce(dispatchValues, 200)(...args),
    [dispatchValues]
  );

  useEffect(() => {
    if (isNotNilOrEmpty(value)) {
      debounceDispatchValues({
        limit: value.length,
        filter: { roleStatus: { NEQ: RoleStatusEnum.Deleted }, id: { IN: value.map(String) } },
      });
    }
  }, [value]);

  const handleSearch = (search: string) => {
    debounceDispatch({
      limit: RECIPIENTS_OPTIONS_LIMIT,
      filter: {
        roleStatus: { NEQ: RoleStatusEnum.Deleted },
        // Only filter by fulltext when something is typed; an empty term must
        // return the default list so the dropdown shows options on focus.
        ...(isNotNilOrEmpty(search) ? { fulltext: { LIKE: search } } : {}),
        ...(isNotNilOrEmpty(roles) ? { role: { IN: roles } } : {}),
      },
    });
  };

  const valuesRecipients = useMemo(
    () =>
      value
        .map(
          (id) =>
            client.readFragment<RecipientFragment>({
              id: `User:${id}`,
              fragment: Recipient,
            }) ?? valuesData?.users.find(propEq("id", id))
        )
        .filter(isNotNil),
    [value, client, valuesData]
  );

  // Options for the already-selected recipients, resolved via the by-id query /
  // Apollo cache. Kept out of the dropdown (that is `options`) and used only to
  // give selected tags a proper label instead of the raw id on first load.
  const valueOptions = useMemo<RecipientsOptions[]>(
    () => valuesRecipients.map(buildRecipientOption),
    [valuesRecipients, buildRecipientOption]
  );

  const isSomeInvalid = useMemo(
    () =>
      valuesRecipients.some(
        ({ availableNotificationChannels }) => intersection(availableNotificationChannels ?? [], channels).length === 0
      ),
    [channels, valuesRecipients]
  );

  return {
    options: options,
    valueOptions: valueOptions,
    loading: loading,
    // Keep the skeleton up for the whole initial fetch of the selected recipients:
    // from mount (before the debounced dispatch) until the first result lands.
    // `valuesCalled` flips true the moment the by-id query is dispatched, so
    // `!valuesCalled` alone would drop the skeleton mid-flight and flash raw ids.
    // Once any data/previousData exists (after the first load) this is false.
    initLoading:
      isNotNilOrEmpty(value) &&
      (!valuesCalled || valuesLoading) &&
      isNil(valuesData) &&
      isNil(valuesPreviousData),
    onSearch: handleSearch,
    isSomeInvalid,
    recipientsData: valuesRecipients,
  };
};
